import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PageHero from "../components/sections/PageHero";
import CTABlock from "../components/sections/CTABlock";
import FAQAccordion from "../components/sections/FAQAccordion";
import { getServiceBySlug } from "../data/services";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);

  useEffect(() => {
    if (service) document.title = service.seoTitle;
  }, [service]);

  if (!service) return <Navigate to="/expertise" replace />;

  return (
    <>
      <PageHero
        eyebrow="Expertise"
        title={service.title}
        subtitle={service.hero}
        breadcrumbs={[
          { label: "Home", to: "/" },
          { label: "Expertise", to: "/expertise" },
          { label: service.title },
        ]}
      />

      {/* OVERVIEW */}
      <section className="bg-ivory section-y" data-testid="service-overview">
        <div className="container-px max-w-[1400px] mx-auto grid md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <div className="overline mb-5">Overview</div>
            <div className="editorial-rule mt-4" />
          </div>
          <div className="md:col-span-7 md:col-start-6">
            {service.overview.map((p, i) => (
              <p
                key={i}
                className="body-lg text-charcoal/80 mb-6 last:mb-0"
                data-testid={`service-overview-p-${i}`}
              >
                {p}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* HOW WE ASSIST */}
      <section className="bg-white border-y border-soft-grey section-y" data-testid="service-assist">
        <div className="container-px max-w-[1400px] mx-auto grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="overline mb-6">How We Assist</div>
            <h2 className="h-display text-4xl md:text-5xl text-navy">
              {service.assistTitle}
            </h2>
          </div>
          <div className="md:col-span-7">
            <ul className="divide-y divide-soft-grey border-t border-b border-soft-grey">
              {service.assist.map((item, i) => (
                <li
                  key={item}
                  data-testid={`service-assist-${i}`}
                  className="flex items-start gap-5 py-5"
                >
                  <span className="font-serif text-gold text-sm pt-1.5 w-8 flex-shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-sans text-base md:text-lg text-charcoal/85">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* MATTERS WE HANDLE */}
      <section className="bg-navy text-ivory section-y" data-testid="service-matters">
        <div className="container-px max-w-[1400px] mx-auto">
          <div className="grid md:grid-cols-12 gap-10 mb-12 md:mb-16">
            <div className="md:col-span-6">
              <div className="overline text-gold mb-6">Representative Matters</div>
              <h2 className="h-display text-4xl md:text-5xl text-ivory">{service.mattersTitle}</h2>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-px bg-ivory/10 border border-ivory/10">
            {service.matters.map((m, i) => (
              <div
                key={m}
                data-testid={`service-matter-${i}`}
                className="bg-navy p-6 md:p-8 hover:bg-deep-ink transition-colors flex items-start gap-4"
              >
                <span className="font-serif text-gold text-xs pt-2 flex-shrink-0">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="text-ivory/90 text-base leading-snug">{m}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO THIS IS FOR */}
      <section className="bg-ivory section-y" data-testid="service-audience">
        <div className="container-px max-w-[1400px] mx-auto grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="overline mb-6">Who This Is For</div>
            <h2 className="h-display text-4xl md:text-5xl text-navy">
              Clients we typically advise.
            </h2>
          </div>
          <div className="md:col-span-7">
            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
              {service.audience.map((a, i) => (
                <li
                  key={a}
                  data-testid={`service-audience-${i}`}
                  className="flex items-start gap-3 text-charcoal/85 text-base md:text-lg py-2 border-b border-soft-grey/70"
                >
                  <span className="mt-2.5 inline-block w-1.5 h-1.5 bg-gold flex-shrink-0" />
                  {a}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* OUR APPROACH */}
      <section className="bg-white border-y border-soft-grey section-y" data-testid="service-approach">
        <div className="container-px max-w-[1400px] mx-auto grid md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <div className="overline mb-5">Our Approach</div>
          </div>
          <div className="md:col-span-7 md:col-start-6">
            <p className="font-serif text-2xl md:text-3xl lg:text-4xl text-navy leading-snug font-light">
              &ldquo;{service.approach}&rdquo;
            </p>
          </div>
        </div>
      </section>

      {/* RELATED SERVICES */}
      <section className="bg-ivory section-y" data-testid="service-related">
        <div className="container-px max-w-[1400px] mx-auto">
          <div className="overline mb-6">Related Services</div>
          <h2 className="h-display text-3xl md:text-4xl text-navy mb-12">
            Explore connected practice areas.
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-soft-grey border border-soft-grey">
            {service.related.map((r, i) => (
              <Link
                key={r.slug}
                to={`/expertise/${r.slug}`}
                data-testid={`service-related-${r.slug}`}
                className="group bg-white p-7 hover:bg-navy hover:text-ivory transition-colors duration-300"
              >
                <div className="font-serif text-gold text-xs mb-5">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="font-serif text-xl text-navy group-hover:text-ivory transition-colors mb-6 leading-snug">
                  {r.title}
                </div>
                <span className="text-[11px] tracking-[0.22em] uppercase border-b border-current pb-1 inline-flex items-center gap-1">
                  Read More <ArrowRight size={12} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white border-t border-soft-grey section-y" data-testid="service-faqs">
        <div className="container-px max-w-[1400px] mx-auto grid md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <div className="overline mb-6">FAQs</div>
            <h2 className="h-display text-3xl md:text-4xl lg:text-5xl text-navy">
              Common questions, clearly answered.
            </h2>
          </div>
          <div className="md:col-span-8">
            <FAQAccordion faqs={service.faqs} />
          </div>
        </div>
      </section>

      <CTABlock />
    </>
  );
}
